import React, { useState, useEffect } from 'react';
import BudgetOperationForm from './BudgetOperationForm';
import BudgetOperationsList from './BudgetOperationsList';
import { loadBudgetOperations, createBudgetOperation, updateBudgetOperation, deleteBudgetOperation } from '../utils/storage';

const BudgetOperationsSection = ({ currentUser, permissions }) => {
  const [operations, setOperations] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingOperation, setEditingOperation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOperations();
  }, []);

  const loadOperations = async () => {
    try {
      setLoading(true);
      
      // Pasar las células del usuario si requiere filtrado por células
      const userCells = currentUser && currentUser.role !== 'admin' && currentUser.cells ? currentUser.cells : null;
      const userRole = currentUser ? currentUser.role : null;
      const userEmail = currentUser ? currentUser.email : null;
      console.log('🔍 BudgetOperationsSection - Cargando con células del usuario:', userCells);
      console.log('🔍 BudgetOperationsSection - Rol del usuario:', userRole);
      console.log('🔍 BudgetOperationsSection - Email del usuario:', userEmail);
      
      const operationsData = await loadBudgetOperations(userCells, userRole, userEmail);
      setOperations(operationsData || []);
    } catch (error) {
      console.error('Error loading budget operations:', error);
      setOperations([]);
    } finally {
      setLoading(false);
    }
  };

  // Las operaciones ya vienen filtradas desde el backend, no necesitamos filtrar aquí
  const filteredOperations = operations;

  const handleNewOperation = () => {
    setEditingOperation(null);
    setShowForm(true);
  };

  const handleEditOperation = (operation) => {
    setEditingOperation(operation);
    setShowForm(true);
  };

  const handleSaveOperation = async (operationData) => {
    try {
      if (editingOperation) {
        await updateBudgetOperation(editingOperation.id, operationData);
        console.log('Operación actualizada:', editingOperation.id);
      } else {
        const response = await createBudgetOperation(operationData);
        console.log('Operación creada:', response.id);
      }
      
      // Recargar las operaciones
      await loadOperations();
      setShowForm(false);
      setEditingOperation(null);
      
      alert(editingOperation ? 'Operación actualizada exitosamente.' : 'Operación creada exitosamente.');
    } catch (error) {
      console.error('Error al guardar operación:', error);
      alert('Error al guardar la operación. Por favor, inténtalo de nuevo.');
    }
  };

  const handleDeleteOperation = async (operationId) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar esta operación? Esta acción no se puede deshacer.')) {
      try {
        await deleteBudgetOperation(operationId);
        await loadOperations();
        alert('Operación eliminada exitosamente.');
      } catch (error) {
        console.error('Error al eliminar operación:', error);
        alert('Error al eliminar la operación. Por favor, inténtalo de nuevo.');
      }
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingOperation(null);
  };

  // Calcular estadísticas usando operaciones filtradas
  const totalOperations = filteredOperations.length;
  const operationsThisMonth = filteredOperations.filter(op => {
    const opDate = new Date(op.fecha_peticion);
    const now = new Date();
    return opDate.getMonth() === now.getMonth() && opDate.getFullYear() === now.getFullYear();
  }).length;
  
  const totalBudget = filteredOperations.reduce((sum, op) => sum + (parseFloat(op.subir_ppto_diario_a) || 0), 0);
  const uniqueCells = [...new Set(filteredOperations.map(op => op.celula))].length;

  if (showForm) {
    return (
      <BudgetOperationForm
        operation={editingOperation}
        onSave={handleSaveOperation}
        onCancel={handleCancelForm}
        currentUser={currentUser}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Header Premium con gradiente */}
        <div className="bg-gradient-to-r from-[#0061A8] to-[#004d8a] rounded-2xl shadow-xl p-6 sm:p-8 mb-8">
          <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center space-y-6 xl:space-y-0">
            <div className="text-white">
              <h1 className="text-3xl sm:text-4xl font-bold mb-2 tracking-tight">
                AUMENTO DE PPTO GOOGLE ADS
              </h1>
              <p className="text-blue-100 text-lg">
                Administra y visualiza operaciones de aumento de presupuesto en Google Ads
              </p>
              <div className="flex items-center space-x-4 mt-2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white/20 text-white">
                  <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                  {totalOperations} operaciones
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white/20 text-white">
                  <div className="w-2 h-2 bg-blue-400 rounded-full mr-2"></div>
                  {operationsThisMonth} este mes
                </span>
              </div>
            </div>
            
            {/* Botón de acción principal */}
            <div className="flex flex-wrap gap-3">
              {permissions.canCreateClients && (
                <button
                  onClick={handleNewOperation}
                  className="group flex items-center space-x-2 bg-gradient-to-r from-[#A8D08D] to-[#8bc34a] text-white py-3 px-6 rounded-xl hover:from-[#8bc34a] hover:to-[#A8D08D] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span className="font-medium">Nueva Operación</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Estadísticas Premium */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="group bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-[#0061A8] transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#63666A] font-medium">Total Operaciones</p>
                <p className="text-3xl font-bold text-[#0061A8] mt-2">{totalOperations}</p>
                <p className="text-xs text-[#63666A] mt-1">operaciones registradas</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-[#0061A8] to-[#004d8a] rounded-xl">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="group bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-[#A8D08D] transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#63666A] font-medium">Este Mes</p>
                <p className="text-3xl font-bold text-[#A8D08D] mt-2">{operationsThisMonth}</p>
                <p className="text-xs text-[#63666A] mt-1">operaciones nuevas</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-[#A8D08D] to-[#8bc34a] rounded-xl">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="group bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-[#63666A] transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#63666A] font-medium">Presupuesto Total</p>
                <p className="text-3xl font-bold text-[#63666A] mt-2">${totalBudget.toLocaleString()}</p>
                <p className="text-xs text-[#63666A] mt-1">suma de presupuestos</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-[#63666A] to-[#4a4d52] rounded-xl">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
            </div>
          </div>

          <div className="group bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-[#D1D3D4] transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#63666A] font-medium">Células Activas</p>
                <p className="text-3xl font-bold text-[#D1D3D4] mt-2">{uniqueCells}</p>
                <p className="text-xs text-[#63666A] mt-1">células diferentes</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-[#D1D3D4] to-[#b8bcc0] rounded-xl">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Lista de operaciones */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0061A8]"></div>
          </div>
        ) : (
          <BudgetOperationsList
            operations={filteredOperations}
            onEdit={handleEditOperation}
            onDelete={handleDeleteOperation}
            permissions={permissions}
            currentUser={currentUser}
          />
        )}
      </div>
    </div>
  );
};

export default BudgetOperationsSection;
